import axios, { Canceler, Method, AxiosError } from 'axios'
import type { Entity } from '../entity'
import { EntityKey } from '../entity'
import errors, { AppError } from '../error'
import { SourceReadOptions, SourceReadResolution } from './source'
import { Selectables } from '../query'
import { Context, InferNative, Type } from '..'
import { BaseActionDefinitionWithInputOutput, HttpTrigger } from '../actionDefinition'
import { environment } from '~/utils/shared/platform'

export interface ServerlessSourceOptions {
  baseUrl: string
  entities: Array<Entity<string, any>>
  actionDefinitions: BaseActionDefinitionWithInputOutput<any, any>[]
  onError?: (error: Error) => void
}

export default function serverlessSource({
  baseUrl,
  entities,
  actionDefinitions,
  onError = (error: Error) => console.error(error),
}: ServerlessSourceOptions) {
  const callAction = <
    InputType extends Type,
    OutputType extends Type,
    Input = InferNative<InputType>,
    Output = InferNative<OutputType>,
  >(
    definition: BaseActionDefinitionWithInputOutput<InputType, OutputType>,
    input: Input,
    context: Context,
    onAbort = () => undefined,
  ) => {
    let isAborted = false

    const firstTrigger = definition.triggers.find(
      trigger => trigger.trigger === 'http',
    ) as HttpTrigger
    if (!firstTrigger) {
      throw errors.incompatible(`Ação "${definition.key}" não pode ser chamada neste método`)
    }

    const headers = {
      Accept: 'application/json',
      ...(context.requestId ? { RequestId: context.requestId } : {}),
      // requestId: context.requestId,
      // debugFiles: context.debugFiles,
    }
    if (environment !== 'production') {
      headers.debugFiles = context.debugFiles
    }
    const url = `${baseUrl}${firstTrigger.route}`

    let abortRequest: Canceler

    return {
      run: async () => {
        try {
          const responsePromise = axios(url, {
            method: firstTrigger.method as Method,
            params: firstTrigger.method === 'GET' ? input : undefined,
            data: firstTrigger.method !== 'GET' ? input : undefined,
            headers: context.token ? { ...headers, Token: context.token } : headers,
            timeout: (definition.runtime.timeout || 15) * 1000,
            cancelToken: new axios.CancelToken(cancel => {
              abortRequest = cancel
            }),
          })
          const { data } = await responsePromise

          if (isAborted) {
            return
          }

          return data
        } catch (axiosError) {
          const error =
            (axiosError as AxiosError).response?.data &&
            typeof (axiosError as AxiosError).response!.data === 'object'
              ? (axiosError as AxiosError<AppError>).response!.data
              : (axiosError as Error)

          if (isAborted) {
            console.error(error)
            const abortError = errors.abort()
            onError(abortError)
            throw abortError
          }

          onError(error)
          throw error
        }
      },
      abort: async () => {
        isAborted = true
        if (abortRequest) {
          abortRequest()
        }
        onAbort()
      },
    }
  }

  // return {
  //   kind: 'source' as 'source',
  //   checkAvailability: async () => {
  //     return navigator.onLine
  //   },
  //   call: callAction,
  // }

  return {
    kind: 'source' as 'source',
    checkAvailability: async () => {
      return navigator.onLine
    },
    call: callAction,
    read: async <
      MainEntityKey extends EntityKey,
      MainEntityKeyAlias extends string,
      Selection extends Selectables = {},
    >({
      query,
      context,
    }: SourceReadOptions<MainEntityKey, MainEntityKeyAlias, Selection>): Promise<
      SourceReadResolution<Selection>
    > => {
      // Entity
      const fromEntity = entities.find(current => current.key === query.entityKey)
      if (!fromEntity) {
        throw errors.incompatible(`Entidade "${query.entityKey}" não existe`)
      }
      const actionDefinition = actionDefinitions.find(
        actionDefinition =>
          actionDefinition.operation === 'read' &&
          actionDefinition.targetEntity === query.entityKey,
      )
      if (!actionDefinition) {
        throw errors.incompatible(`Ação de leitura para entidade "${query.entityKey}" não existe`)
      }
      const { run, abort } = callAction(actionDefinition, query, context)
      return { run, abort } as any
    },
  }
}
